var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import produce from 'immer';
import { cloneDeep } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useReducer } from 'react';
import { Button, ButtonMode, ButtonSize, ButtonVariant } from '../Components/Button';
import { ButtonGroup } from '../Components/ButtonGroup';
import { DatePicker, FieldIconMode } from '../Components/DatePicker';
import { MaskedTextField } from '../Components/MaskedTextField';
import { useDebounce } from '../Hooks/useDebounce';
import { invalidDate } from '../Medications.en';
import { ValidDateFormats } from '../Shared/DateUtilities';
import { alphaNumericInputFilter } from '../Shared/InputFilters';
import { ApplicationStorageKeys, loadFromLocalStorage, saveToLocalStorage } from '../Shared/LocalStorage';
import { divider } from '../Styles/Colours';
import styles from './PharmacyRequestsSummaryFilter.module.css';
var filterIsValidate = function (_a) {
    var startsOn = _a.startsOn, endsOn = _a.endsOn;
    if (!!startsOn && !!endsOn) {
        var startsOnMoment = moment(startsOn, ValidDateFormats.IsoFormat);
        var endsOnMoment = moment(endsOn, ValidDateFormats.IsoFormat);
        if (startsOnMoment && endsOnMoment && startsOnMoment.isValid() && endsOnMoment.isValid()) {
            var startOfDayMoment = startsOnMoment.local().startOf('day');
            var endOfDayMoment = endsOnMoment.local().endOf('day');
            if (startOfDayMoment.isAfter(endOfDayMoment)) {
                return false;
            }
        }
    }
    return true;
};
export var convertFilterToQueryObject = function (filter) { return ({
    medicationName: !filter.medicationName || filter.medicationName.trim() === "" ? null : filter.medicationName,
    encounterPlace: filter.encounterPlace,
    startsOn: filter.startsOn ? moment(filter.startsOn).local().format(ValidDateFormats.IsoFormat) : null,
    expiresOn: filter.endsOn ? moment(filter.endsOn).local().format(ValidDateFormats.IsoFormat) : null
}); };
var clearFilterDefaults = {
    medicationName: '',
    encounterPlace: null,
    startsOn: '',
    endsOn: '',
    limit: 50
};
var filterReducer = produce(function (state, action) {
    switch (action.type) {
        case 'UpdateField':
            state[action.field] = action.value;
            break;
        case 'UpdateLimit':
            state.limit = action.value;
            break;
        case 'Clear':
            return cloneDeep(clearFilterDefaults);
    }
});
export function usePharmacyRequestsListFilter(filterUpdatedCallback) {
    var _a = __read(useReducer(filterReducer, loadFromLocalStorage(ApplicationStorageKeys.PharmacyRequestsListFilters, cloneDeep(clearFilterDefaults))), 2), state = _a[0], dispatch = _a[1];
    useEffect(function () {
        saveToLocalStorage(ApplicationStorageKeys.PharmacyRequestsListFilters, state);
    }, [state]);
    var debouncedState = useDebounce(state);
    useEffect(function () {
        if (filterIsValidate(debouncedState))
            filterUpdatedCallback(debouncedState);
    }, [debouncedState]);
    var updateField = function (field) { return function (value) {
        dispatch({ type: 'UpdateField', field: field, value: value });
    }; };
    var updateLimit = function (value) { return function () {
        dispatch({ type: 'UpdateLimit', value: value });
    }; };
    var clearField = function (field, clearValue) { return function () {
        dispatch({ type: 'UpdateField', field: field, value: clearValue });
    }; };
    var clearFilter = function () {
        dispatch({ type: 'Clear' });
    };
    var refresh = function () {
        if (filterIsValidate(debouncedState))
            filterUpdatedCallback(debouncedState);
    };
    return {
        state: state,
        updateField: updateField,
        clearField: clearField,
        updateLimit: updateLimit,
        refresh: refresh,
        clearFilter: clearFilter
    };
}
export function PharmacyRequestsSummaryFilter(props) {
    return (React.createElement("div", { className: styles.containFilters },
        React.createElement("div", { className: styles.controls },
            React.createElement(MaskedTextField, { name: 'medicationName', label: 'Medication name', testId: 'text-input-medication-name', value: props.state.medicationName, filterFunction: alphaNumericInputFilter, onChange: props.updateField('medicationName'), onClear: props.clearField('medicationName', '') }),
            React.createElement("div", { className: styles.filterDatesRow },
                React.createElement(DatePicker, { label: 'Created from', value: props.state.startsOn ? moment(props.state.startsOn) : null, errorText: !filterIsValidate(props.state) ? invalidDate : '', onChange: function (date) {
                        props.updateField('startsOn')(date ? date.local().startOf('day').format() : '');
                    }, onClear: function () {
                        props.updateField('startsOn')('');
                    }, testId: 'date-picker-filter-from', calendarIconMode: FieldIconMode.Embed, fillContainer: true }),
                React.createElement("span", { className: styles.filterSpaceBetweenDate }),
                React.createElement(DatePicker, { label: 'Created to', errorText: !filterIsValidate(props.state) ? invalidDate : '', value: props.state.endsOn ? moment(props.state.endsOn) : null, onChange: function (date) {
                        props.updateField('endsOn')(date ? date.local().startOf('day').format() : '');
                    }, onClear: function () {
                        props.updateField('endsOn')('');
                    }, testId: 'date-picker-filter-to', calendarIconMode: FieldIconMode.Embed, fillContainer: true })),
            React.createElement("div", { className: styles.sectionTitle }, "Show max results"),
            React.createElement("div", { className: styles.metricPeriodPicker },
                React.createElement(ButtonGroup, { borderColour: divider },
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 25 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 25 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(25), testId: 'filter-records-25' }, "25"),
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 50 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 50 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(50), testId: 'filter-records-50' }, "50"),
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 100 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 100 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(100), testId: 'filter-records-100' }, "100"),
                    React.createElement(Button, { size: ButtonSize.small, width: '25%', mode: props.state.limit === 0 ? ButtonMode.solid : ButtonMode.border, variant: props.state.limit === 0 ? ButtonVariant.primary : ButtonVariant.light, onClick: props.updateLimit(0), testId: 'filter-records-all' }, "All")))),
        React.createElement("div", { className: styles.clearButtonContainer },
            React.createElement(Button, { onClick: props.clearFilter, width: '100%', size: ButtonSize.small, variant: ButtonVariant.light, testId: 'clear-filter', color: 'primary' }, "Clear all filters"))));
}
